/**
 * Tenant theme contract (B0.38).
 *
 * The TenantThemeProvider holds one of these per tenant. Editors mutate
 * it via the admin hook; the provider writes CSS variables + the
 * `<html data-theme="...">` attribute every time it changes; downstream
 * components re-render only when they consume `useTenantTheme()`.
 */

export type ThemeMode = "light" | "dark" | "system";

/** Six recolourable design-system slots — every variant the UI surfaces. */
export interface PaletteOverride {
  /** RGB triple as "R G B" (so Tailwind's `rgb(var(--x) / <alpha>)` works). */
  brand?: string | null;
  accent?: string | null;
  success?: string | null;
  warning?: string | null;
  danger?: string | null;
  info?: string | null;
}

/** Font-stack preset. Swaps the `--font-sans` CSS variable. */
export type TypographyPreset = "inter" | "plex" | "roboto" | "source";

/** Density preset. Scales padding/gap via `--density-scale`. */
export type DensityPreset = "compact" | "comfortable" | "spacious";

/** Corner-radius preset. Scales `--radius-scale`. */
export type RadiusPreset = "sharp" | "soft" | "round";

/** Background pattern preset. Sets `--bg-pattern` + the `data-pattern` attr. */
export type BackgroundPattern = "none" | "dots" | "grid" | "topo";

export interface TenantTheme {
  mode: ThemeMode;
  /** Display name shown in the top bar + auth pages. */
  productName: string;
  /** Data-URL (Phase 0) / CDN URL (Phase 1) of the white-label logo. */
  logoUrl: string | null;
  palette: PaletteOverride;
  typography: TypographyPreset;
  density: DensityPreset;
  radius: RadiusPreset;
  pattern: BackgroundPattern;
  /** Which industry packs the tenant has enabled. */
  enabledPacks: string[];
  /** Optional preset id this theme started from (for "currently active preset" UI). */
  presetId?: string;
}

/**
 * The product's name in the UI, beside the logo, everywhere: "Foundry Twin" (Arjun, 2026-10-06). "Sahas Forge" is the
 * internal name. No theme, preset, vendor brand, browser setting or console changes it (lib/tenant-theme.tsx).
 */
export const PRODUCT_NAME = "Foundry Twin";

export const DEFAULT_THEME: TenantTheme = {
  mode: "system",
  productName: PRODUCT_NAME,
  logoUrl: null,
  // Default palette: "Graphite" — neutral steel / industrial (slate), replacing
  // the previous Foundry-Amber orange (which read as non-industrial). brand =
  // slate-600, accent = slate-500; the status slots stay null so success/warning/
  // danger/info keep their meaningful green/amber/red/blue CSS defaults. Mirrors
  // the "graphite" theme preset; a tenant can still pick any palette.
  palette: {
    brand: "71 85 105",
    accent: "100 116 139",
    success: null,
    warning: null,
    danger: null,
    info: null,
  },
  typography: "inter",
  density: "comfortable",
  radius: "soft",
  pattern: "none",
  enabledPacks: ["foundry"],
  presetId: "graphite",
};

/** Mapping from typography preset → CSS font-family stack. */
export const TYPOGRAPHY_STACKS: Record<TypographyPreset, string> = {
  inter:
    '"Inter Variable", "Inter", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  plex:
    '"IBM Plex Sans", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
  roboto:
    'Roboto, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
  source:
    '"Source Sans 3", "Source Sans Pro", ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
};

export const TYPOGRAPHY_LABEL: Record<TypographyPreset, string> = {
  inter: "Inter (default)",
  plex: "IBM Plex Sans",
  roboto: "Roboto",
  source: "Source Sans 3",
};

export const DENSITY_SCALE: Record<DensityPreset, number> = {
  compact: 0.85,
  comfortable: 1.0,
  spacious: 1.15,
};

export const RADIUS_SCALE: Record<RadiusPreset, number> = {
  sharp: 0.35,
  soft: 1.0,
  round: 1.6,
};

export const PATTERN_LABEL: Record<BackgroundPattern, string> = {
  none: "None",
  dots: "Dot grid",
  grid: "Blueprint",
  topo: "Topography",
};
